import { installBrowserOutboundIsolation } from "./outbound-isolation";

export const STALE_CHUNK_ERROR_OPERANDS = [
	"Failed to fetch dynamically imported module",
	"error loading dynamically imported module",
	"Importing a module script failed",
	"Unable to preload CSS",
	"reading 'universal'",
	"reading 'component'",
	"evaluating '",
	".universal'",
	".component'",
	'can\'t access property "universal"',
	'can\'t access property "component"'
] as const;

const [
	dynamicImportFetch,
	dynamicImportLoad,
	moduleScriptFailed,
	preloadCss,
	v8Universal,
	v8Component,
	jscEvaluating,
	jscUniversal,
	jscComponent,
	spiderMonkeyUniversal,
	spiderMonkeyComponent
] = STALE_CHUNK_ERROR_OPERANDS;

export function isStaleChunkError(error: unknown): boolean {
	const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";

	return (
		message.includes(dynamicImportFetch) ||
		message.includes(dynamicImportLoad) ||
		message.includes(moduleScriptFailed) ||
		message.includes(preloadCss) ||
		message.includes(v8Universal) ||
		message.includes(v8Component) ||
		(message.includes(jscEvaluating) &&
			(message.includes(jscUniversal) || message.includes(jscComponent))) ||
		message.includes(spiderMonkeyUniversal) ||
		message.includes(spiderMonkeyComponent)
	);
}

export function isBrowserExtensionError(error: unknown): boolean {
	const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
	return message.startsWith("Invalid call to runtime.sendMessage()");
}

const NETWORK_FETCH_MESSAGES = [
	"Failed to fetch",
	"Load failed",
	"NetworkError when attempting to fetch resource"
] as const;
const NETWORK_FETCH_FAILURE_FINGERPRINT = "network-fetch-failure";
const NETWORK_FETCH_FAILURE_MESSAGE = "Network fetch failure";

export type ClientKitState = {
	updated: { current: boolean; check(): Promise<boolean> };
	navigating: { type: string | null };
};

export type SentryEvent = {
	fingerprint?: string[];
	tags?: Record<string, unknown>;
	contexts?: Record<string, unknown>;
	exception?: {
		values?: {
			value?: string;
			stacktrace?: { frames?: { filename?: string | null }[] };
		}[];
	};
};

type ErrorWithName = { name: string; message: string };

function isTypeError(error: unknown): error is ErrorWithName {
	if (error instanceof TypeError) return true;
	if (typeof error !== "object" || error === null || !("name" in error) || !("message" in error))
		return false;
	return error.name === "TypeError" && typeof error.message === "string";
}

export function normalizeNetworkFetchMessage(error: unknown): string | null {
	if (!isTypeError(error)) return null;
	const stripped = error.message
		.trim()
		.replace(/\s*\([^()]*\)$/, "")
		.replace(/\.$/, "")
		.trim();
	return NETWORK_FETCH_MESSAGES.some((message) => message === stripped)
		? NETWORK_FETCH_FAILURE_MESSAGE
		: null;
}

export type NetworkFetchDiagnostics = {
	onLine: boolean | null;
	effectiveType: string | null;
	visibilityState: string | null;
	buildIsCurrent: boolean | null;
	navigationType: string | null;
};

type NetworkInformation = { effectiveType?: string };

function readOrNull<T>(get: () => T): NonNullable<T> | null {
	try {
		return get() ?? null;
	} catch {
		return null;
	}
}

export function collectNetworkFetchDiagnostics(kit: ClientKitState): NetworkFetchDiagnostics {
	return {
		onLine: readOrNull(() => navigator.onLine),
		effectiveType: readOrNull(
			() =>
				(navigator as Navigator & { connection?: NetworkInformation }).connection
					?.effectiveType
		),
		visibilityState: readOrNull(() => document.visibilityState),
		buildIsCurrent: readOrNull(() => !kit.updated.current),
		navigationType: readOrNull(() => kit.navigating.type ?? "none")
	};
}

export function shapeNetworkFetchEvent(
	event: SentryEvent,
	error: unknown,
	kit: ClientKitState
): boolean {
	const normalized = normalizeNetworkFetchMessage(error);
	if (!normalized) return false;
	event.fingerprint = [NETWORK_FETCH_FAILURE_FINGERPRINT];
	const primary = event.exception?.values?.[0];
	if (primary?.value) {
		event.tags = { ...event.tags, originalMessage: primary.value };
		primary.value = normalized;
	}
	const diagnostics = collectNetworkFetchDiagnostics(kit);
	event.tags = {
		...event.tags,
		...Object.fromEntries(
			Object.entries(diagnostics).map(([key, value]) => [
				key,
				value === null ? "unknown" : String(value)
			])
		)
	};
	event.contexts = { ...event.contexts, connectivity: diagnostics };
	return true;
}

type NavigateEventFields = {
	destination: { sameDocument: boolean };
	downloadRequest: string | null;
};

const PAGE_LEAVE_WINDOW_MS = 3_000;

export function createPageLeaveTracker() {
	let pageLeftAt: number | null = null;

	function recordPageLeave(event: NavigateEventFields): void {
		if (event.destination.sameDocument || event.downloadRequest !== null) return;
		pageLeftAt = Date.now();
	}

	function clearPageLeave(): void {
		pageLeftAt = null;
	}

	function isFetchCancelledByPageLeave(error: unknown): boolean {
		if (pageLeftAt === null || Date.now() - pageLeftAt > PAGE_LEAVE_WINDOW_MS) return false;
		const cause =
			typeof error === "object" && error !== null && "cause" in error
				? error.cause
				: undefined;
		return (
			normalizeNetworkFetchMessage(error) !== null ||
			normalizeNetworkFetchMessage(cause) !== null
		);
	}

	return { recordPageLeave, clearPageLeave, isFetchCancelledByPageLeave };
}

export type ClientErrorInput<TEvent extends { url: URL }> = {
	error: unknown;
	event: TEvent;
	kind: string;
};

export type ClientErrorOutput = { message: string; sentryEventId?: string };
export type ClientErrorHandler<TEvent extends { url: URL }> = (
	input: ClientErrorInput<TEvent>
) => ClientErrorOutput | undefined | Promise<ClientErrorOutput | undefined>;

export type ClientSentryOptions = {
	dsn: string;
	tunnel: string;
	tracesSampleRate: number;
	integrations: unknown[];
	replaysSessionSampleRate: number;
	replaysOnErrorSampleRate: number;
	environment: string;
	release: string;
	initialScope: { tags: Record<string, string> };
	beforeSend: (event: SentryEvent, hint: { originalException?: unknown }) => SentryEvent | null;
};

export type ClientSentry<TEvent extends { url: URL }> = {
	init(options: ClientSentryOptions): void;
	thirdPartyErrorFilterIntegration(options: {
		filterKeys: string[];
		behaviour: "drop-error-if-exclusively-contains-third-party-frames";
	}): unknown;
	captureException(error: Error, context: { tags: Record<string, boolean> }): void;
	lastEventId(): string | undefined;
	handleErrorWithSentry(handler: ClientErrorHandler<TEvent>): ClientErrorHandler<TEvent>;
	getClient(): { getIntegrationByName(name: string): unknown } | undefined;
	replayIntegration(options: {
		maskAllText: boolean;
		blockAllMedia: boolean;
		useCompression: boolean;
		maskAllInputs: boolean;
	}): unknown;
	addIntegration(integration: unknown): void;
};

export type ClientErrorLifecycleOptions<TEvent extends { url: URL }> = {
	dev: boolean;
	service: "fit" | "www";
	buildVersion: string;
	sentryDsn: string;
	kit: ClientKitState & { page: { route: { id: string | null } } };
	isErrorWithStatus(error: unknown): error is { status: number; message: string };
	sentry: ClientSentry<TEvent>;
};

const STALE_CHUNK_KEY = "stale-chunk-reload";
const STALE_CHUNK_REPORTED_KEY = "stale-chunk-reported";
const STALE_CHUNK_GUARD_SETTLE_MS = 30_000;
const VERSION_CHECK_COOLDOWN_MS = 10_000;
const PARTYTOWN_FILENAME = "/~partytown/";
const SENTRY_APPLICATION_KEY = "appfergmarcom";

export function createClientErrorLifecycle<TEvent extends { url: URL }>(
	options: ClientErrorLifecycleOptions<TEvent>
): { handleError: ClientErrorHandler<TEvent> } {
	const { dev, service, buildVersion, sentryDsn, kit, sentry } = options;
	const browserIsolated = typeof window !== "undefined" && installBrowserOutboundIsolation();
	const pageLeave = createPageLeaveTracker();
	let reloadedInPriorLoad = (() => {
		try {
			return !!sessionStorage.getItem(STALE_CHUNK_KEY);
		} catch {
			return false;
		}
	})();
	let staleChunkSeenThisLoad = false;
	let versionCheckInFlight: Promise<boolean> | null = null;
	let versionCheckedAt = 0;
	const preloadFetchFailures = new WeakSet<object>();

	function guardedReload(targetUrl?: URL): boolean {
		if (browserIsolated && targetUrl && targetUrl.origin !== window.location.origin)
			return false;
		staleChunkSeenThisLoad = true;
		try {
			if (!sessionStorage.getItem(STALE_CHUNK_KEY)) {
				sessionStorage.setItem(STALE_CHUNK_KEY, "1");
				if (targetUrl) window.location.assign(targetUrl.href);
				else window.location.reload();
				return true;
			}
		} catch {
			return false;
		}
		return false;
	}

	function handleStaleChunk(error: unknown, targetUrl?: URL): void {
		if (guardedReload(targetUrl)) return;
		if (!reloadedInPriorLoad) return;
		try {
			if (sessionStorage.getItem(STALE_CHUNK_REPORTED_KEY)) return;
			sessionStorage.setItem(STALE_CHUNK_REPORTED_KEY, "1");
		} catch {
			return;
		}
		const message = error instanceof Error ? error.message : String(error);
		sentry.captureException(
			new Error(`Persistent chunk load failure after reload: ${message}`),
			{
				tags: { staleChunkReloadFailed: true }
			}
		);
	}

	async function checkForUpdatedBuild(force = false): Promise<boolean> {
		if (kit.updated.current) return true;
		if (versionCheckInFlight) return versionCheckInFlight;
		if (!force && Date.now() - versionCheckedAt < VERSION_CHECK_COOLDOWN_MS) return false;
		versionCheckInFlight = kit.updated
			.check()
			.catch(() => false)
			.finally(() => {
				versionCheckInFlight = null;
				versionCheckedAt = Date.now();
			});
		return versionCheckInFlight;
	}

	function handleUnclassifiedError(error: unknown): void {
		void checkForUpdatedBuild().then((stale) => {
			if (stale) handleStaleChunk(error);
		});
	}

	if (typeof window !== "undefined") {
		setTimeout(() => {
			if (staleChunkSeenThisLoad) return;
			reloadedInPriorLoad = false;
			try {
				sessionStorage.removeItem(STALE_CHUNK_KEY);
				sessionStorage.removeItem(STALE_CHUNK_REPORTED_KEY);
			} catch {
				return;
			}
		}, STALE_CHUNK_GUARD_SETTLE_MS);

		if ("navigation" in window)
			window.navigation.addEventListener("navigate", pageLeave.recordPageLeave);
		window.addEventListener("pageshow", (event) => {
			if (event.persisted) pageLeave.clearPageLeave();
		});
		window.addEventListener("vite:preloadError", (event) => {
			if (guardedReload()) event.preventDefault();
		});
		window.addEventListener("unhandledrejection", (event) => {
			if (isStaleChunkError(event.reason)) handleStaleChunk(event.reason);
			else handleUnclassifiedError(event.reason);
		});
		window.addEventListener("error", (event) => {
			if (isStaleChunkError(event.error)) handleStaleChunk(event.error);
			else handleUnclassifiedError(event.error);
		});
	}

	if (!dev && !browserIsolated)
		sentry.init({
			dsn: sentryDsn,
			tunnel: "/api/sentry",
			tracesSampleRate: dev ? 1.0 : 0.1,
			integrations: [
				sentry.thirdPartyErrorFilterIntegration({
					filterKeys: [SENTRY_APPLICATION_KEY],
					behaviour: "drop-error-if-exclusively-contains-third-party-frames"
				})
			],
			replaysSessionSampleRate: 0,
			replaysOnErrorSampleRate: dev ? 0 : 1.0,
			environment: dev ? "development" : "production",
			release: buildVersion,
			initialScope: { tags: { service, version: buildVersion } },
			beforeSend(event, hint) {
				const error = hint.originalException;
				if (
					error instanceof Error &&
					error.message.includes("ResizeObserver loop limit exceeded")
				)
					return null;
				if (navigator.userAgent.includes("Lightpanda")) return null;
				if (error instanceof Error && error.message.includes("Java object is gone"))
					return null;
				if (isBrowserExtensionError(error)) return null;
				if (
					event.exception?.values?.some((value) =>
						value.stacktrace?.frames?.some((frame) =>
							frame.filename?.includes(PARTYTOWN_FILENAME)
						)
					)
				)
					return null;
				if (isStaleChunkError(error) && !event.tags?.staleChunkReloadFailed) return null;
				if (pageLeave.isFetchCancelledByPageLeave(error)) return null;
				if (typeof error === "object" && error !== null && preloadFetchFailures.has(error))
					return null;
				shapeNetworkFetchEvent(event, error, kit);
				return event;
			}
		});

	if (!dev && !browserIsolated) {
		const loadReplay = () => {
			if (sentry.getClient()?.getIntegrationByName("Replay")) return;
			sentry.addIntegration(
				sentry.replayIntegration({
					maskAllText: false,
					blockAllMedia: false,
					useCompression: false,
					maskAllInputs: false
				})
			);
		};
		if ("requestIdleCallback" in window) requestIdleCallback(loadReplay);
		else setTimeout(loadReplay, 3_000);
	}

	const myErrorHandler: ClientErrorHandler<TEvent> = (input) => {
		console.error(input.error, input.event);
		const caught = options.isErrorWithStatus(input.error) ? input.error : undefined;
		const captured = input.kind === "unknown" || (caught !== undefined && caught.status >= 500);
		return {
			message:
				input.kind === "unknown"
					? "An unexpected error occurred"
					: (caught?.message ?? "An unexpected error occurred"),
			sentryEventId: captured ? sentry.lastEventId() : undefined
		};
	};
	const sentryErrorHandler = browserIsolated
		? myErrorHandler
		: sentry.handleErrorWithSentry(myErrorHandler);

	const handleError: ClientErrorHandler<TEvent> = async (input) => {
		if (isTypeError(input.error) && normalizeNetworkFetchMessage(input.error)) {
			const currentUrl = new URL(window.location.href);
			if (
				kit.page.route.id !== null &&
				kit.navigating.type === null &&
				(input.event.url.origin !== currentUrl.origin ||
					input.event.url.pathname !== currentUrl.pathname ||
					input.event.url.search !== currentUrl.search)
			) {
				preloadFetchFailures.add(input.error);
			} else {
				preloadFetchFailures.delete(input.error);
			}
		}
		if (isStaleChunkError(input.error)) {
			handleStaleChunk(input.error, input.event.url);
			return;
		}
		if (
			kit.navigating.type === null &&
			input.event.url.href === window.location.href &&
			(await checkForUpdatedBuild(true))
		) {
			handleStaleChunk(input.error, input.event.url);
			return;
		}
		return sentryErrorHandler(input);
	};

	return { handleError };
}
